import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, useTheme, Autocomplete, TextField, MenuItem, IconButton, Collapse,
  Tabs, Tab, LinearProgress, Stack, Tooltip, Avatar, Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon, Search as SearchIcon, FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon, Print as PrintIcon, ExpandMore, ExpandLess,
  CheckCircle, RadioButtonUnchecked, ShoppingCart, Receipt, Assignment,
  AccountTree, AttachMoney, Insights, Storefront
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { branchesService } from '../../services/branches.service';
import { custodiesService } from '../../services/custodies.service';
import axiosInstance from '../../services/axiosInstance';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// ─────────── Types ───────────────────────────────────────────────────
interface ItemLine {
  itemId: number; itemCode: string; itemName: string; itemNameAr: string;
  unitId: number; unitName: string; unitNameAr: string;
  requestedQuantity: number; approvedQuantity: number | null;
  unitPrice: number; lineTotal: number; priority: string | null;
}
interface Row {
  requestId: number; requestNumber: string;
  requestDate: string; requiredDate: string | null;
  status: string;
  branchId: number; branchName: string; branchNameAr: string;
  custodyId: number | null; custodyNumber: string | null; custodyHolder: string | null;
  approvedDate: string | null; approvedByName: string | null;
  linesCount: number; totalRequestedQty: number; totalApprovedQty: number;
  purchaseOrderId: number | null; pONumber: string | null; poDate: string | null;
  poStatus: string | null; poSubTotal: number | null; poTotal: number | null;
  purchaseInvoiceId: number | null; invoiceNumber: string | null; invoiceDate: string | null;
  invoiceStatus: string | null; invoiceTotal: number | null;
  journalEntryId: number | null; journalEntryNumber: string | null; journalEntryStatus: string | null;
  stageRequested: boolean; stageApproved: boolean; stagePOCreated: boolean;
  stageInvoiceCreated: boolean; stageInvoicePosted: boolean;
  daysFromRequestToApproval: number | null; daysFromApprovalToPO: number | null; daysFromPOToInvoice: number | null;
  items: ItemLine[];
}
interface BranchSummary {
  branchId: number; branchName: string; branchNameAr: string;
  requestsCount: number; approvedCount: number; fulfilledCount: number;
  poSCount: number; invoicesCount: number; invoicesPostedCount: number;
  totalValue: number; postedValue: number;
}
interface ItemSummary {
  itemId: number; itemCode: string; itemName: string; itemNameAr: string;
  unitName: string; unitNameAr: string;
  totalRequestedQty: number; totalApprovedQty: number;
  averageUnitPrice: number; minUnitPrice: number; maxUnitPrice: number;
  totalValue: number; requestedInBranches: number; requestsCount: number;
}
interface Summary {
  totalRequests: number; totalBranches: number; totalUniqueItems: number;
  totalLines: number; totalPOs: number; totalInvoices: number; totalInvoicesPosted: number;
  totalRequestedValue: number; totalInvoicedValue: number; totalPostedValue: number;
  statusBreakdown: Record<string, number>;
  avgDaysRequestToApproval: number | null; avgDaysApprovalToPO: number | null; avgDaysPOToInvoice: number | null;
}
interface ReportResult {
  generatedAt: string;
  fromDate: string | null; toDate: string | null;
  custodyId: number | null; custodyName: string | null;
  branchId: number | null; branchName: string | null;
  statusFilter: string | null;
  rows: Row[]; byBranch: BranchSummary[]; byItem: ItemSummary[]; summary: Summary;
}

// ─────────── Component ────────────────────────────────────────────────
const BPRWorkflowReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const { minDate, maxDate } = useFiscalDateRange();

  const [result, setResult] = useState<ReportResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [branches, setBranches] = useState<any[]>([]);
  const [custodies, setCustodies] = useState<any[]>([]);

  // Filters
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [custodyFilter, setCustodyFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Tabs
  const [activeTab, setActiveTab] = useState(0);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    branchesService.getAll().then(setBranches).catch(() => {});
    custodiesService.getAll().then((c: any) => setCustodies(c.filter((x: any) => x.custodyType === 'Central'))).catch(() => {});
  }, []);

  const loadReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromDate', `${fromDate.getFullYear()}-${String(fromDate.getMonth() + 1).padStart(2, '0')}-${String(fromDate.getDate()).padStart(2, '0')}`);
      if (toDate) params.append('toDate', `${toDate.getFullYear()}-${String(toDate.getMonth() + 1).padStart(2, '0')}-${String(toDate.getDate()).padStart(2, '0')}`);
      if (custodyFilter) params.append('custodyId', custodyFilter.toString());
      if (branchFilter) params.append('branchId', branchFilter.toString());
      if (statusFilter !== 'All') params.append('status', statusFilter);
      const { data } = await axiosInstance.get(`/BPRWorkflowReport?${params.toString()}`);
      setResult(data);
      toast.success(isRTL ? 'تم التحميل' : 'Loaded');
    } catch {
      toast.error(isRTL ? 'فشل التحميل' : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n: number | null | undefined) => (n == null ? '-' : Number(n).toFixed(3));
  const fmtDate = (s: string | null | undefined) => s ? new Date(s).toLocaleDateString('en-GB') : '-';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
  const reportTitle = isRTL ? 'تقرير دورة طلبات الشراء الفرعية' : 'Branch Purchase Requests Workflow Report';

  // Filter rows by search term
  const filteredRows = useMemo(() => {
    if (!result) return [];
    if (!searchTerm) return result.rows;
    const term = searchTerm.toLowerCase();
    return result.rows.filter(r =>
      r.requestNumber.toLowerCase().includes(term) ||
      r.branchName.toLowerCase().includes(term) ||
      (r.branchNameAr || '').includes(searchTerm) ||
      (r.pONumber || '').toLowerCase().includes(term) ||
      (r.invoiceNumber || '').toLowerCase().includes(term) ||
      (r.custodyHolder || '').toLowerCase().includes(term)
    );
  }, [result, searchTerm]);

  const toggleExpand = (id: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const statusColor = (status: string): string => {
    switch (status) {
      case 'Draft': return '#9E9E9E';
      case 'Submitted': return '#FF9800';
      case 'Approved': return '#2196F3';
      case 'Fulfilled': return '#4CAF50';
      case 'Rejected': return '#F44336';
      case 'Posted': return '#2E7D32';
      default: return '#757575';
    }
  };
  const statusLabel = (status: string): string => {
    if (!isRTL) return status;
    const map: Record<string, string> = {
      'Draft': 'مسودة', 'Submitted': 'مقدّم', 'Approved': 'معتمد',
      'Fulfilled': 'منفّذ', 'Rejected': 'مرفوض', 'Posted': 'مرحّل', 'Received': 'مستلم'
    };
    return map[status] || status;
  };

  // Pipeline stage indicator (BPR → Approve → PO → Invoice → Post)
  const PipelineStage = ({ row }: { row: Row }) => {
    const stages = [
      { done: row.stageRequested, label: isRTL ? 'سند' : 'BPR', color: '#3f51b5', icon: <Assignment sx={{ fontSize: 14 }} /> },
      { done: row.stageApproved, label: isRTL ? 'اعتماد' : 'Approve', color: '#2196F3', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
      { done: row.stagePOCreated, label: isRTL ? 'أمر' : 'PO', color: '#FF9800', icon: <ShoppingCart sx={{ fontSize: 14 }} /> },
      { done: row.stageInvoiceCreated, label: isRTL ? 'فاتورة' : 'Invoice', color: '#9C27B0', icon: <Receipt sx={{ fontSize: 14 }} /> },
      { done: row.stageInvoicePosted, label: isRTL ? 'ترحيل' : 'Posted', color: '#2E7D32', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
    ];
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {stages.map((s, i) => (
          <React.Fragment key={i}>
            <Tooltip title={s.label} arrow>
              <Avatar sx={{
                bgcolor: s.done ? s.color : (isDark ? '#424242' : '#e0e0e0'),
                color: s.done ? 'white' : (isDark ? '#888' : '#999'),
                width: 22, height: 22, fontSize: 11
              }}>
                {s.icon}
              </Avatar>
            </Tooltip>
            {i < stages.length - 1 && (
              <Box sx={{
                height: 2, width: 12,
                bgcolor: stages[i + 1].done && s.done ? stages[i + 1].color : (isDark ? '#424242' : '#e0e0e0')
              }} />
            )}
          </React.Fragment>
        ))}
      </Box>
    );
  };

  // ─────────── Excel Export ──────────────────────────────────────────
  const exportToExcel = async () => {
    if (!result) return;
    try {
      const wb = new ExcelJS.Workbook();
      // Sheet 1: Summary
      const ws1 = wb.addWorksheet(isRTL ? 'الملخص' : 'Summary');
      ws1.mergeCells('A1:F1');
      ws1.getCell('A1').value = compName || '';
      ws1.getCell('A1').font = { size: 18, bold: true, color: { argb: 'FF1565C0' } };
      ws1.getCell('A1').alignment = { horizontal: 'center' };

      ws1.mergeCells('A2:F2');
      ws1.getCell('A2').value = reportTitle;
      ws1.getCell('A2').font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      ws1.getCell('A2').fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      ws1.getCell('A2').alignment = { horizontal: 'center' };

      const summaryRows: [string, any][] = [
        [isRTL ? 'إجمالي السندات' : 'Total Requests', result.summary.totalRequests],
        [isRTL ? 'إجمالي الفروع' : 'Total Branches', result.summary.totalBranches],
        [isRTL ? 'أصناف فريدة' : 'Unique Items', result.summary.totalUniqueItems],
        [isRTL ? 'إجمالي الأسطر' : 'Total Lines', result.summary.totalLines],
        [isRTL ? 'أوامر الشراء' : 'Total POs', result.summary.totalPOs],
        [isRTL ? 'الفواتير' : 'Total Invoices', result.summary.totalInvoices],
        [isRTL ? 'الفواتير المرحّلة' : 'Posted Invoices', result.summary.totalInvoicesPosted],
        [isRTL ? 'القيمة المطلوبة (KWD)' : 'Requested Value (KWD)', result.summary.totalRequestedValue],
        [isRTL ? 'قيمة الفواتير (KWD)' : 'Invoiced Value (KWD)', result.summary.totalInvoicedValue],
        [isRTL ? 'القيمة المرحّلة (KWD)' : 'Posted Value (KWD)', result.summary.totalPostedValue],
      ];
      summaryRows.forEach((r, i) => {
        const row = ws1.getRow(4 + i);
        row.getCell(1).value = r[0];
        row.getCell(1).font = { bold: true };
        row.getCell(2).value = r[1];
      });
      ws1.columns = [{ width: 32 }, { width: 18 }];

      // Sheet 2: Workflow rows
      const ws2 = wb.addWorksheet(isRTL ? 'دورة العمل' : 'Workflow');
      const wfHeaders = [
        '#', isRTL ? 'رقم السند' : 'Request #', isRTL ? 'الفرع' : 'Branch',
        isRTL ? 'العهدة' : 'Custody', isRTL ? 'التاريخ' : 'Date',
        isRTL ? 'تاريخ التسليم' : 'Required', isRTL ? 'الحالة' : 'Status',
        isRTL ? 'أصناف' : 'Items', isRTL ? 'الكمية' : 'Qty',
        isRTL ? 'رقم PO' : 'PO #', isRTL ? 'تاريخ PO' : 'PO Date',
        isRTL ? 'قيمة PO' : 'PO Value',
        isRTL ? 'رقم الفاتورة' : 'Invoice #', isRTL ? 'تاريخ الفاتورة' : 'Inv Date',
        isRTL ? 'قيمة الفاتورة' : 'Invoice Value', isRTL ? 'حالة الفاتورة' : 'Inv Status',
        isRTL ? 'القيد' : 'JE #', isRTL ? 'حالة القيد' : 'JE Status',
      ];
      const hr = ws2.getRow(1);
      wfHeaders.forEach((h, i) => {
        const c = hr.getCell(i + 1);
        c.value = h;
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
        c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      });
      filteredRows.forEach((r, idx) => {
        const row = ws2.getRow(2 + idx);
        row.getCell(1).value = idx + 1;
        row.getCell(2).value = r.requestNumber;
        row.getCell(3).value = isRTL ? r.branchNameAr || r.branchName : r.branchName;
        row.getCell(4).value = r.custodyHolder || '-';
        row.getCell(5).value = fmtDate(r.requestDate);
        row.getCell(6).value = fmtDate(r.requiredDate);
        row.getCell(7).value = statusLabel(r.status);
        row.getCell(8).value = r.linesCount;
        row.getCell(9).value = Number(r.totalRequestedQty);
        row.getCell(10).value = r.pONumber || '-';
        row.getCell(11).value = fmtDate(r.poDate);
        row.getCell(12).value = r.poTotal != null ? Number(r.poTotal) : '-';
        row.getCell(13).value = r.invoiceNumber || '-';
        row.getCell(14).value = fmtDate(r.invoiceDate);
        row.getCell(15).value = r.invoiceTotal != null ? Number(r.invoiceTotal) : '-';
        row.getCell(16).value = r.invoiceStatus ? statusLabel(r.invoiceStatus) : '-';
        row.getCell(17).value = r.journalEntryNumber || '-';
        row.getCell(18).value = r.journalEntryStatus ? statusLabel(r.journalEntryStatus) : '-';
      });
      ws2.columns = [
        { width: 5 }, { width: 14 }, { width: 22 }, { width: 24 }, { width: 12 }, { width: 12 },
        { width: 12 }, { width: 8 }, { width: 10 }, { width: 14 }, { width: 12 }, { width: 14 },
        { width: 14 }, { width: 12 }, { width: 14 }, { width: 12 }, { width: 14 }, { width: 12 }
      ];

      // Sheet 3: Items detail (line by line)
      const ws3 = wb.addWorksheet(isRTL ? 'تفاصيل الأصناف' : 'Item Lines');
      const lineHeaders = [
        '#', isRTL ? 'رقم السند' : 'Request',
        isRTL ? 'الفرع' : 'Branch', isRTL ? 'الصنف' : 'Item',
        isRTL ? 'الوحدة' : 'Unit', isRTL ? 'مطلوب' : 'Req Qty',
        isRTL ? 'معتمد' : 'App Qty', isRTL ? 'سعر الوحدة' : 'Unit Price',
        isRTL ? 'الإجمالي' : 'Total',
      ];
      const hr3 = ws3.getRow(1);
      lineHeaders.forEach((h, i) => {
        const c = hr3.getCell(i + 1);
        c.value = h;
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      });
      let lineIdx = 0;
      filteredRows.forEach(r => {
        r.items.forEach(li => {
          lineIdx++;
          const row = ws3.getRow(1 + lineIdx);
          row.getCell(1).value = lineIdx;
          row.getCell(2).value = r.requestNumber;
          row.getCell(3).value = isRTL ? r.branchNameAr || r.branchName : r.branchName;
          row.getCell(4).value = `${li.itemCode} - ${isRTL ? li.itemNameAr : li.itemName}`;
          row.getCell(5).value = isRTL ? li.unitNameAr || li.unitName : li.unitName;
          row.getCell(6).value = Number(li.requestedQuantity);
          row.getCell(7).value = li.approvedQuantity != null ? Number(li.approvedQuantity) : '-';
          row.getCell(8).value = Number(li.unitPrice);
          row.getCell(9).value = Number(li.lineTotal);
        });
      });
      ws3.columns = [
        { width: 5 }, { width: 14 }, { width: 22 }, { width: 36 }, { width: 12 },
        { width: 12 }, { width: 12 }, { width: 14 }, { width: 14 }
      ];

      // Sheet 4: By Branch
      const ws4 = wb.addWorksheet(isRTL ? 'حسب الفرع' : 'By Branch');
      const bHeaders = [
        isRTL ? 'الفرع' : 'Branch', isRTL ? 'سندات' : 'Requests',
        isRTL ? 'معتمدة' : 'Approved', isRTL ? 'منفّذة' : 'Fulfilled',
        isRTL ? 'POs' : 'POs', isRTL ? 'فواتير' : 'Invoices',
        isRTL ? 'مرحّلة' : 'Posted', isRTL ? 'القيمة (KWD)' : 'Value (KWD)',
      ];
      const hr4 = ws4.getRow(1);
      bHeaders.forEach((h, i) => {
        const c = hr4.getCell(i + 1);
        c.value = h;
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      });
      result.byBranch.forEach((b, i) => {
        const row = ws4.getRow(2 + i);
        row.getCell(1).value = isRTL ? b.branchNameAr || b.branchName : b.branchName;
        row.getCell(2).value = b.requestsCount;
        row.getCell(3).value = b.approvedCount;
        row.getCell(4).value = b.fulfilledCount;
        row.getCell(5).value = b.poSCount;
        row.getCell(6).value = b.invoicesCount;
        row.getCell(7).value = b.invoicesPostedCount;
        row.getCell(8).value = Number(b.totalValue);
      });
      ws4.columns = [{ width: 28 }, { width: 10 }, { width: 10 }, { width: 10 }, { width: 8 }, { width: 10 }, { width: 10 }, { width: 16 }];

      // Sheet 5: By Item
      const ws5 = wb.addWorksheet(isRTL ? 'حسب الصنف' : 'By Item');
      const iHeaders = [
        isRTL ? 'الكود' : 'Code', isRTL ? 'الصنف' : 'Item',
        isRTL ? 'الوحدة' : 'Unit', isRTL ? 'كمية مطلوبة' : 'Total Req',
        isRTL ? 'كمية معتمدة' : 'Total App', isRTL ? 'متوسط السعر' : 'Avg Price',
        isRTL ? 'أقل سعر' : 'Min', isRTL ? 'أعلى سعر' : 'Max',
        isRTL ? 'القيمة (KWD)' : 'Value (KWD)', isRTL ? 'فروع' : 'Branches',
      ];
      const hr5 = ws5.getRow(1);
      iHeaders.forEach((h, i) => {
        const c = hr5.getCell(i + 1);
        c.value = h;
        c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };
      });
      result.byItem.forEach((it, i) => {
        const row = ws5.getRow(2 + i);
        row.getCell(1).value = it.itemCode;
        row.getCell(2).value = isRTL ? it.itemNameAr || it.itemName : it.itemName;
        row.getCell(3).value = isRTL ? it.unitNameAr || it.unitName : it.unitName;
        row.getCell(4).value = Number(it.totalRequestedQty);
        row.getCell(5).value = Number(it.totalApprovedQty);
        row.getCell(6).value = Number(it.averageUnitPrice);
        row.getCell(7).value = Number(it.minUnitPrice);
        row.getCell(8).value = Number(it.maxUnitPrice);
        row.getCell(9).value = Number(it.totalValue);
        row.getCell(10).value = it.requestedInBranches;
      });
      ws5.columns = [{ width: 14 }, { width: 28 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 10 }, { width: 10 }, { width: 14 }, { width: 10 }];

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `BPRWorkflow_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch {
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  // ─────────── PDF Export ────────────────────────────────────────────
  const exportToPdf = () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pw = doc.internal.pageSize.width;
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(20); doc.setTextColor(21, 101, 192);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(21, 101, 192); doc.roundedRect(pw / 2 - 90, 32, 180, 12, 3, 3, 'F');
      doc.setFontSize(13); doc.setTextColor(255, 255, 255);
      doc.text('BPR Workflow Report', pw / 2, 39.5, { align: 'center' });

      // Summary KPIs band
      let startY = 52;
      autoTable(doc, {
        body: [[
          { content: `Requests\n${result.summary.totalRequests}`, styles: { halign: 'center', fillColor: [33, 150, 243], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', cellPadding: 4 } },
          { content: `Branches\n${result.summary.totalBranches}`, styles: { halign: 'center', fillColor: [76, 175, 80], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', cellPadding: 4 } },
          { content: `POs\n${result.summary.totalPOs}`, styles: { halign: 'center', fillColor: [255, 152, 0], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', cellPadding: 4 } },
          { content: `Invoices\n${result.summary.totalInvoices}`, styles: { halign: 'center', fillColor: [156, 39, 176], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', cellPadding: 4 } },
          { content: `Total KWD\n${fmt(result.summary.totalInvoicedValue || result.summary.totalRequestedValue)}`, styles: { halign: 'center', fillColor: [198, 40, 40], textColor: [255, 255, 255], fontSize: 10, fontStyle: 'bold', cellPadding: 4 } },
        ]],
        startY,
        theme: 'plain',
      });
      startY = (doc as any).lastAutoTable.finalY + 5;

      autoTable(doc, {
        head: [['Request #', 'Branch', 'Custody', 'Date', 'Required', 'Status', 'Items', 'Qty', 'PO #', 'PO Value', 'Invoice #', 'Inv Value', 'Inv Status']],
        body: filteredRows.map(r => [
          r.requestNumber,
          r.branchName,
          r.custodyHolder || '-',
          fmtDate(r.requestDate),
          fmtDate(r.requiredDate),
          r.status,
          r.linesCount,
          fmt(r.totalRequestedQty),
          r.pONumber || '-',
          fmt(r.poTotal),
          r.invoiceNumber || '-',
          fmt(r.invoiceTotal),
          r.invoiceStatus || '-',
        ]),
        startY,
        theme: 'grid',
        headStyles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontSize: 9, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 8 },
      });

      doc.save(`BPRWorkflow_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch {
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  // ─────────── Print ─────────────────────────────────────────────────
  const handlePrint = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=1400,height=900'); if (!w) return;

    const rowsHtml = filteredRows.map(r => `
      <tr>
        <td><b>${r.requestNumber}</b></td>
        <td>${isRTL ? r.branchNameAr || r.branchName : r.branchName}</td>
        <td>${r.custodyHolder || '-'}</td>
        <td>${fmtDate(r.requestDate)}</td>
        <td>${fmtDate(r.requiredDate)}</td>
        <td><span style="background:${statusColor(r.status)};color:white;padding:2px 8px;border-radius:8px;font-size:9px">${statusLabel(r.status)}</span></td>
        <td style="text-align:center">${r.linesCount}</td>
        <td style="text-align:right"><b>${fmt(r.totalRequestedQty)}</b></td>
        <td>${r.pONumber || '-'}</td>
        <td style="text-align:right">${fmt(r.poTotal)}</td>
        <td>${r.invoiceNumber || '-'}</td>
        <td style="text-align:right;color:#1565C0;font-weight:bold">${fmt(r.invoiceTotal)}</td>
        <td>${r.invoiceStatus ? `<span style="background:${statusColor(r.invoiceStatus)};color:white;padding:2px 6px;border-radius:6px;font-size:9px">${statusLabel(r.invoiceStatus)}</span>` : '-'}</td>
      </tr>`).join('');

    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:15px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1565C0;padding-bottom:10px;margin-bottom:10px}
      .header img{max-height:60px} .header h1{color:#1565C0;margin:0;font-size:20px}
      .kpis{display:grid;grid-template-columns:repeat(5,1fr);gap:8px;margin:12px 0}
      .kpi{background:linear-gradient(135deg,#1565C0,#42A5F5);color:white;padding:10px;border-radius:8px;text-align:center}
      .kpi b{font-size:18px;display:block} .kpi small{opacity:0.9;font-size:10px}
      table{width:100%;border-collapse:collapse;font-size:10px;margin-top:6px}
      th{background:#1565C0;color:white;padding:6px;text-align:${isRTL ? 'right' : 'left'};border:1px solid #1565C0;font-size:10px}
      td{padding:5px 6px;border:1px solid #ddd}
      tr:nth-child(even){background:#fafafa}
      @media print{body{margin:8px}}
    </style></head><body>
    <div class="header">
      <div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
      <div style="text-align:center;flex:1">
        <h1>${reportTitle}</h1>
        <div style="color:#666;font-size:11px">${compName || ''}</div>
        <div style="color:#666;font-size:10px;margin-top:2px">
          ${result.fromDate ? `${isRTL ? 'من' : 'From'}: ${fmtDate(result.fromDate)}` : ''}
          ${result.toDate ? ` &nbsp; ${isRTL ? 'إلى' : 'To'}: ${fmtDate(result.toDate)}` : ''}
          ${result.custodyName ? ` &nbsp; ${isRTL ? 'العهدة' : 'Custody'}: ${result.custodyName}` : ''}
        </div>
      </div>
      <div style="width:80px"></div>
    </div>
    <div class="kpis">
      <div class="kpi"><b>${result.summary.totalRequests}</b><small>${isRTL ? 'سندات' : 'Requests'}</small></div>
      <div class="kpi" style="background:linear-gradient(135deg,#2E7D32,#66BB6A)"><b>${result.summary.totalBranches}</b><small>${isRTL ? 'فروع' : 'Branches'}</small></div>
      <div class="kpi" style="background:linear-gradient(135deg,#FF8C00,#FFA500)"><b>${result.summary.totalPOs}</b><small>${isRTL ? 'أوامر شراء' : 'POs'}</small></div>
      <div class="kpi" style="background:linear-gradient(135deg,#6A1B9A,#AB47BC)"><b>${result.summary.totalInvoices}</b><small>${isRTL ? 'فواتير' : 'Invoices'}</small></div>
      <div class="kpi" style="background:linear-gradient(135deg,#C62828,#EF5350)"><b>${fmt(result.summary.totalInvoicedValue || result.summary.totalRequestedValue)}</b><small>KWD</small></div>
    </div>
    <table>
      <thead><tr>
        <th>${isRTL ? 'رقم السند' : 'Request #'}</th>
        <th>${isRTL ? 'الفرع' : 'Branch'}</th>
        <th>${isRTL ? 'العهدة' : 'Custody'}</th>
        <th>${isRTL ? 'التاريخ' : 'Date'}</th>
        <th>${isRTL ? 'تسليم' : 'Required'}</th>
        <th>${isRTL ? 'الحالة' : 'Status'}</th>
        <th>${isRTL ? 'أصناف' : 'Items'}</th>
        <th>${isRTL ? 'الكمية' : 'Qty'}</th>
        <th>${isRTL ? 'PO #' : 'PO #'}</th>
        <th>${isRTL ? 'قيمة PO' : 'PO Value'}</th>
        <th>${isRTL ? 'فاتورة #' : 'Invoice #'}</th>
        <th>${isRTL ? 'قيمة الفاتورة' : 'Inv Value'}</th>
        <th>${isRTL ? 'حالة الفاتورة' : 'Inv Status'}</th>
      </tr></thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div style="text-align:center;margin-top:14px;color:#999;font-size:9px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  // ─────────── Render ────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {reportTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL
              ? 'تتبع كامل لدورة طلبات الشراء الفرعية: السند → الاعتماد → أمر الشراء → الفاتورة → الترحيل المحاسبي'
              : 'End-to-end tracking: Request → Approval → Purchase Order → Invoice → Posted Entry'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={loadReport} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', px: 3, borderRadius: 3 }}>
            {loading ? (isRTL ? 'جاري...' : 'Loading...') : (isRTL ? 'عرض' : 'Load')}
          </Button>
          {result && (<>
            <Button variant="contained" size="small" startIcon={<ExcelIcon />} onClick={exportToExcel}
              sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>Excel</Button>
            <Button variant="contained" size="small" startIcon={<PdfIcon />} onClick={exportToPdf}
              sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint}
              sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>
              {isRTL ? 'طباعة' : 'Print'}
            </Button>
          </>)}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={isRTL ? 'من تاريخ' : 'From'} value={fromDate} onChange={setFromDate}
              format="dd/MM/yyyy" minDate={minDate} maxDate={toDate || maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
            <DatePicker label={isRTL ? 'إلى تاريخ' : 'To'} value={toDate} onChange={setToDate}
              format="dd/MM/yyyy" minDate={fromDate || minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <Autocomplete size="small" sx={{ minWidth: 220 }}
            options={[{ id: '' as number | '', label: isRTL ? 'كل العهد' : 'All Custodies' },
              ...custodies.map((c: any) => ({ id: c.custodyId, label: `${c.custodyNumber} - ${c.employeeName || ''}` }))]}
            getOptionLabel={o => o.label} isOptionEqualToValue={(o, v) => o.id === v.id}
            value={[{ id: '' as number | '', label: isRTL ? 'كل العهد' : 'All Custodies' },
              ...custodies.map((c: any) => ({ id: c.custodyId, label: `${c.custodyNumber} - ${c.employeeName || ''}` }))].find(o => o.id === custodyFilter) || { id: '' as number | '', label: isRTL ? 'كل العهد' : 'All Custodies' }}
            onChange={(_, v) => setCustodyFilter(v?.id || '')}
            renderInput={p => <TextField {...p} label={isRTL ? 'العهدة' : 'Custody'} />} />
          <Autocomplete size="small" sx={{ minWidth: 200 }}
            options={[{ id: '' as number | '', label: isRTL ? 'كل الفروع' : 'All Branches' },
              ...branches.map((b: any) => ({ id: b.branchId, label: isRTL ? b.branchNameAr || b.branchName : b.branchName }))]}
            getOptionLabel={o => o.label} isOptionEqualToValue={(o, v) => o.id === v.id}
            value={[{ id: '' as number | '', label: isRTL ? 'كل الفروع' : 'All Branches' },
              ...branches.map((b: any) => ({ id: b.branchId, label: isRTL ? b.branchNameAr || b.branchName : b.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: isRTL ? 'كل الفروع' : 'All Branches' }}
            onChange={(_, v) => setBranchFilter(v?.id || '')}
            renderInput={p => <TextField {...p} label={isRTL ? 'الفرع' : 'Branch'} />} />
          <TextField size="small" select label={isRTL ? 'الحالة' : 'Status'} value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="All">{isRTL ? 'الكل' : 'All'}</MenuItem>
            <MenuItem value="Draft">{isRTL ? 'مسودة' : 'Draft'}</MenuItem>
            <MenuItem value="Submitted">{isRTL ? 'مقدّم' : 'Submitted'}</MenuItem>
            <MenuItem value="Approved">{isRTL ? 'معتمد' : 'Approved'}</MenuItem>
            <MenuItem value="Fulfilled">{isRTL ? 'منفّذ' : 'Fulfilled'}</MenuItem>
            <MenuItem value="Rejected">{isRTL ? 'مرفوض' : 'Rejected'}</MenuItem>
          </TextField>
          <TextField size="small" placeholder={isRTL ? 'بحث (سند/PO/فاتورة/فرع...)' : 'Search (BPR/PO/Inv/Branch...)'}
            value={searchTerm} onChange={e => setSearchTerm(e.target.value)} sx={{ minWidth: 280 }}
            InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }} />
        </Stack>
      </Paper>

      {result && (
        <>
          {/* KPI Dashboard */}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 2, mb: 3 }}>
            {[
              { label: isRTL ? 'إجمالي السندات' : 'Total Requests', value: result.summary.totalRequests, color: ['#1565C0', '#42A5F5'], icon: <Assignment /> },
              { label: isRTL ? 'الفروع' : 'Branches', value: result.summary.totalBranches, color: ['#2E7D32', '#66BB6A'], icon: <Storefront /> },
              { label: isRTL ? 'أوامر الشراء' : 'Purchase Orders', value: result.summary.totalPOs, color: ['#FF8C00', '#FFA726'], icon: <ShoppingCart /> },
              { label: isRTL ? 'الفواتير' : 'Invoices', value: result.summary.totalInvoices, color: ['#6A1B9A', '#AB47BC'], icon: <Receipt /> },
              { label: isRTL ? 'مرحّلة' : 'Posted', value: result.summary.totalInvoicesPosted, color: ['#00796B', '#26A69A'], icon: <CheckCircle /> },
              { label: isRTL ? 'القيمة (KWD)' : 'Value (KWD)', value: fmt(result.summary.totalInvoicedValue || result.summary.totalRequestedValue), color: ['#C62828', '#EF5350'], icon: <AttachMoney /> },
            ].map((kpi, i) => (
              <Card key={i} sx={{ background: `linear-gradient(135deg, ${kpi.color[0]} 0%, ${kpi.color[1]} 100%)`, color: 'white', borderRadius: 3 }}>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography variant="caption" sx={{ opacity: 0.9 }}>{kpi.label}</Typography>
                      <Typography variant="h5" fontWeight="bold">{kpi.value}</Typography>
                    </Box>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>{kpi.icon}</Avatar>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>

          {/* Cycle time card */}
          {(result.summary.avgDaysRequestToApproval != null || result.summary.avgDaysApprovalToPO != null || result.summary.avgDaysPOToInvoice != null) && (
            <Paper sx={{ p: 2, mb: 3, borderRadius: 3, background: isDark ? '#1e293b' : '#f8fafc' }}>
              <Stack direction="row" spacing={3} alignItems="center" justifyContent="space-around" flexWrap="wrap">
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{isRTL ? 'متوسط أيام الاعتماد' : 'Avg. Days to Approval'}</Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary">{result.summary.avgDaysRequestToApproval?.toFixed(1) ?? '-'}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{isRTL ? 'متوسط أيام إصدار PO' : 'Avg. Days to PO'}</Typography>
                  <Typography variant="h6" fontWeight="bold" color="warning.main">{result.summary.avgDaysApprovalToPO?.toFixed(1) ?? '-'}</Typography>
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">{isRTL ? 'متوسط أيام إصدار الفاتورة' : 'Avg. Days to Invoice'}</Typography>
                  <Typography variant="h6" fontWeight="bold" color="secondary">{result.summary.avgDaysPOToInvoice?.toFixed(1) ?? '-'}</Typography>
                </Box>
              </Stack>
            </Paper>
          )}

          {/* Tabs */}
          <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
            <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} variant="scrollable">
              <Tab icon={<AccountTree />} iconPosition="start" label={isRTL ? 'دورة العمل' : 'Workflow'} />
              <Tab icon={<Storefront />} iconPosition="start" label={isRTL ? 'حسب الفرع' : 'By Branch'} />
              <Tab icon={<Insights />} iconPosition="start" label={isRTL ? 'حسب الصنف' : 'By Item'} />
            </Tabs>

            {/* Tab 1: Workflow */}
            {activeTab === 0 && (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', width: 40 }} />
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'رقم السند' : 'Request #'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الفرع' : 'Branch'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'العهدة' : 'Custody'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'تاريخ التسليم' : 'Required'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'مراحل' : 'Pipeline'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'أصناف' : 'Items'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'PO' : 'PO'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الفاتورة' : 'Invoice'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'القيمة' : 'Value'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredRows.map(r => (
                      <React.Fragment key={r.requestId}>
                        <TableRow hover sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
                          <TableCell>
                            <IconButton size="small" onClick={() => toggleExpand(r.requestId)}>
                              {expanded.has(r.requestId) ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{r.requestNumber}</TableCell>
                          <TableCell>{isRTL ? r.branchNameAr || r.branchName : r.branchName}</TableCell>
                          <TableCell>
                            <Typography variant="caption">{r.custodyHolder || '-'}</Typography>
                          </TableCell>
                          <TableCell>{fmtDate(r.requiredDate)}</TableCell>
                          <TableCell>
                            <Chip size="small" label={statusLabel(r.status)}
                              sx={{ bgcolor: statusColor(r.status), color: 'white', fontWeight: 'bold', minWidth: 80 }} />
                          </TableCell>
                          <TableCell><PipelineStage row={r} /></TableCell>
                          <TableCell align="right"><b>{r.linesCount}</b></TableCell>
                          <TableCell>
                            {r.pONumber ? (
                              <Box>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#FF8C00' }}>{r.pONumber}</Typography>
                                <br />
                                <Typography variant="caption" color="text.secondary">{fmtDate(r.poDate)}</Typography>
                              </Box>
                            ) : <Typography variant="caption" color="text.disabled">-</Typography>}
                          </TableCell>
                          <TableCell>
                            {r.invoiceNumber ? (
                              <Box>
                                <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, color: '#9C27B0' }}>{r.invoiceNumber}</Typography>
                                <br />
                                <Chip size="small" label={statusLabel(r.invoiceStatus || '-')}
                                  sx={{ bgcolor: statusColor(r.invoiceStatus || ''), color: 'white', fontSize: 9, height: 16 }} />
                              </Box>
                            ) : <Typography variant="caption" color="text.disabled">-</Typography>}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>
                            {fmt(r.invoiceTotal || r.poTotal)}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell colSpan={11} sx={{ p: 0, border: 0 }}>
                            <Collapse in={expanded.has(r.requestId)} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2, bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#fafafa' }}>
                                <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                                  {isRTL ? 'تفاصيل الأصناف' : 'Items Detail'}
                                </Typography>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>#</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'الكود' : 'Code'}</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                                      <TableCell sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'كمية مطلوبة' : 'Req Qty'}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'كمية معتمدة' : 'App Qty'}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'سعر الوحدة' : 'Unit Price'}</TableCell>
                                      <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: 11 }}>{isRTL ? 'الإجمالي' : 'Total'}</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {r.items.map((li, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell sx={{ fontSize: 11 }}>{idx + 1}</TableCell>
                                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11 }}>{li.itemCode}</TableCell>
                                        <TableCell sx={{ fontSize: 11 }}>{isRTL ? li.itemNameAr || li.itemName : li.itemName}</TableCell>
                                        <TableCell sx={{ fontSize: 11 }}>{isRTL ? li.unitNameAr || li.unitName : li.unitName}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(li.requestedQuantity)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: 11 }}>{fmt(li.approvedQuantity)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: 11, color: '#1565C0' }}>{fmt(li.unitPrice)}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: 11, fontWeight: 'bold', color: '#2E7D32' }}>{fmt(li.lineTotal)}</TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 2: By Branch */}
            {activeTab === 1 && (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الفرع' : 'Branch'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'سندات' : 'Requests'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'معتمدة' : 'Approved'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'منفّذة' : 'Fulfilled'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>POs</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'فواتير' : 'Invoices'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'مرحّلة' : 'Posted'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'القيمة (KWD)' : 'Value (KWD)'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.byBranch.map(b => (
                      <TableRow key={b.branchId} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{isRTL ? b.branchNameAr || b.branchName : b.branchName}</TableCell>
                        <TableCell align="right">{b.requestsCount}</TableCell>
                        <TableCell align="right"><Chip size="small" label={b.approvedCount} sx={{ bgcolor: '#2196F3', color: 'white', minWidth: 36 }} /></TableCell>
                        <TableCell align="right"><Chip size="small" label={b.fulfilledCount} sx={{ bgcolor: '#4CAF50', color: 'white', minWidth: 36 }} /></TableCell>
                        <TableCell align="right">{b.poSCount}</TableCell>
                        <TableCell align="right">{b.invoicesCount}</TableCell>
                        <TableCell align="right">{b.invoicesPostedCount}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{fmt(b.totalValue)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Tab 3: By Item */}
            {activeTab === 2 && (
              <TableContainer sx={{ maxHeight: '70vh' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الكود' : 'Code'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الصنف' : 'Item'}</TableCell>
                      <TableCell sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'كمية مطلوبة' : 'Req Qty'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'متوسط السعر' : 'Avg Price'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'أقل' : 'Min'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'أعلى' : 'Max'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'القيمة' : 'Value'}</TableCell>
                      <TableCell align="right" sx={{ bgcolor: '#1565C0', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'فروع' : 'Branches'}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.byItem.map(it => (
                      <TableRow key={it.itemId} hover>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{it.itemCode}</TableCell>
                        <TableCell>{isRTL ? it.itemNameAr || it.itemName : it.itemName}</TableCell>
                        <TableCell>{isRTL ? it.unitNameAr || it.unitName : it.unitName}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(it.totalRequestedQty)}</TableCell>
                        <TableCell align="right" sx={{ color: '#1565C0' }}>{fmt(it.averageUnitPrice)}</TableCell>
                        <TableCell align="right" sx={{ color: '#2E7D32' }}>{fmt(it.minUnitPrice)}</TableCell>
                        <TableCell align="right" sx={{ color: '#C62828' }}>{fmt(it.maxUnitPrice)}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0' }}>{fmt(it.totalValue)}</TableCell>
                        <TableCell align="right">{it.requestedInBranches}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      {!result && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <AccountTree sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            {isRTL ? 'حدد الفلاتر واضغط "عرض" لتحميل التقرير' : 'Set filters and click "Load" to load the report'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default BPRWorkflowReport;
